import React, { useState, useCallback, useEffect } from 'react';
import { SurveyFormatRef } from '~app/components/Survey/SurveyFormat';
import useLayoutEffect from '~app/hooks/useIsomorphicLayoutEffect';

declare global {
  namespace SM {
    const __OFFSET_TOP_OF_SURVEY__: number | undefined;
  }
}

const useCenterContainerOffset = (
  centerTarget: SurveyFormatRef | undefined,
  mainContainer: React.RefObject<HTMLDivElement>,
  viewportExclusions: React.RefObject<HTMLElement>[],
  heightExclusions: React.RefObject<HTMLElement>[],
  adjustment?: (margin: number) => number
): number => {
  /**
   * Global variable set in template during the initial rendering
   * (to reduce layout change)
   *
   * _will never be set during SSR render_
   */
  const preReactOffsetTop = globalThis.SM?.__OFFSET_TOP_OF_SURVEY__;
  const [offsetTop, setOffsetTop] = useState<number>(preReactOffsetTop || 0);
  const calculateOffsetTop = useCallback(() => {
    const winH = window.innerHeight;
    const targetContainer = centerTarget?.ref;
    const targetContainerHeight = targetContainer?.current?.offsetHeight ?? 0;
    if (!targetContainer || targetContainerHeight === 0) {
      // do not calculate offset if ref is not set yet
      return;
    }
    const viewport = winH - viewportExclusions.reduce((a, ref) => a + (ref.current?.offsetHeight || 0), 0);
    const excludeHeight = heightExclusions.reduce((a, ref) => a + (ref.current?.offsetHeight || 0), 0);
    let margin = targetContainerHeight > 0 ? Math.max(10, viewport / 2 - targetContainerHeight / 2 - excludeHeight) : 0;
    margin = adjustment?.(margin) || margin;
    setOffsetTop(margin);
  }, [centerTarget, viewportExclusions, heightExclusions, adjustment]);

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateOffsetTop);
    return () => window.removeEventListener('resize', calculateOffsetTop);
  }, [calculateOffsetTop]);

  // Remove `marginTop` set during boot-up on template from `mainContainer`
  useLayoutEffect(() => {
    if (mainContainer.current?.style.marginTop) {
      // eslint-disable-next-line no-param-reassign
      mainContainer.current.style.marginTop = '';
    }
  }, [mainContainer]);

  useEffect(() => {
    calculateOffsetTop();
  }, [centerTarget, calculateOffsetTop, viewportExclusions, heightExclusions]);

  return offsetTop;
};

export default useCenterContainerOffset;
